import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Send, Edit, Trash2, Bell, CheckCircle, Copy, MessageSquare, ThumbsDown, MapPin, Eraser } from "lucide-react";
import { toast } from "sonner";

function formatFecha(iso) {
  if (!iso) return "-";
  return iso.split("T")[0].split("-").reverse().join("/");
}

function esPasada(fecha) {
  if (!fecha) return false;
  return fecha.split("T")[0] <= new Date().toISOString().split("T")[0];
}

function waLink(telefono, mensaje) {
  const num = telefono.replace(/\D/g, "");
  return `https://wa.me/${num}?text=${encodeURIComponent(mensaje)}`;
}

export default function Notificaciones() {
  const [tab, setTab] = useState("pendientes");
  const [pendientes, setPendientes] = useState([]);
  const [todas, setTodas] = useState([]);

  // Diálogo editar mensaje
  const [editOpen, setEditOpen] = useState(false);
  const [editingNotif, setEditingNotif] = useState(null);
  const [mensaje, setMensaje] = useState("");

  // Diálogo enviar
  const [enviarOpen, setEnviarOpen] = useState(false);
  const [notifSeleccionada, setNotifSeleccionada] = useState(null);
  const [copiado, setCopiado] = useState(false);

  const load = async () => {
    try {
      const [pRes, tRes] = await Promise.all([
        axios.get(`${API}/notificaciones/pendientes`),
        axios.get(`${API}/notificaciones/todas`),
      ]);
      setPendientes(pRes.data);
      setTodas(tRes.data);
    } catch { toast.error("Error al cargar notificaciones"); }
  };

  useEffect(() => { load(); }, []);

  const abrirEnviar = (n) => {
    setNotifSeleccionada(n);
    setCopiado(false);
    setEnviarOpen(true);
  };

  const copiarMensaje = () => {
    navigator.clipboard.writeText(notifSeleccionada.mensaje);
    setCopiado(true);
    setTimeout(() => setCopiado(false), 2000);
  };

  const marcarEnviado = async () => {
    try {
      await axios.put(`${API}/notificaciones/${notifSeleccionada.id}/marcar-enviado`);
      toast.success("Marcado como enviado");
      setEnviarOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al marcar");
    }
  };

  const handleEditOpen = (n) => {
    setEditingNotif(n);
    setMensaje(n.mensaje);
    setEditOpen(true);
  };

  const handleSaveMensaje = async () => {
    try {
      await axios.put(`${API}/notificaciones/${editingNotif.id}/mensaje`, { mensaje });
      toast.success("Mensaje actualizado");
      setEditOpen(false);
      load();
    } catch { toast.error("Error al guardar mensaje"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar esta notificación?")) return;
    try {
      await axios.delete(`${API}/notificaciones/${id}`);
      toast.success("Notificación eliminada");
      load();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleRechazar = async (n) => {
    const confirm = window.confirm(
      `¿Registrar que ${n.cliente_nombre} rechazó su compra de ${n.medicamento_nombre}?\n\nSi ya tiene 1 rechazo previo, su inscripción quedará inactiva por 6 meses.`
    );
    if (!confirm) return;
    try {
      const { data } = await axios.put(`${API}/notificaciones/${n.id}/rechazar`);
      if (data.inactivada) {
        toast.warning(
          `Inscripción inactivada por 6 meses. Se reactivará el ${data.fecha_reactivacion}.`,
          { duration: 6000 }
        );
      } else {
        toast.info(`Rechazo #${data.rechazos} registrado. Al próximo rechazo la inscripción se inactivará por 6 meses.`);
      }
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al registrar rechazo");
    }
  };

  const handleLimpiarVencidas = async () => {
    const ok = window.confirm(
      "¿Eliminar todas las notificaciones pendientes con más de 60 días de atraso?\n\nEsto no afecta las inscripciones, solo limpia notificaciones antiguas (compras muy viejas que ya no aplican)."
    );
    if (!ok) return;
    try {
      const { data } = await axios.delete(`${API}/notificaciones/limpiar-vencidas`);
      toast.success(`${data.eliminadas} notificaciones vencidas eliminadas`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al limpiar");
    }
  };

  const hoyStr = new Date().toISOString().split("T")[0];
  const vencidas = pendientes.filter(n => n.fecha_programada <= hoyStr);

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Notificaciones</h1>
          <p className="text-sm text-slate-500">
            {pendientes.length} pendientes
            {vencidas.length > 0 && <span className="text-red-600 font-medium"> · {vencidas.length} para enviar hoy</span>}
          </p>
        </div>
        {pendientes.some(n => {
          const corte = new Date();
          corte.setDate(corte.getDate() - 60);
          return n.fecha_programada < corte.toISOString().split("T")[0];
        }) && (
          <Button
            variant="outline"
            onClick={handleLimpiarVencidas}
            className="border-slate-200 text-slate-500 hover:text-red-600 hover:border-red-200 text-xs h-8"
          >
            <Eraser className="w-3.5 h-3.5 mr-1.5" /> Limpiar vencidas (+60 días)
          </Button>
        )}
      </div>

      {vencidas.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Bell className="w-5 h-5 text-red-600 shrink-0" />
          <p className="text-sm font-medium text-red-700">
            Tienes {vencidas.length} notificación{vencidas.length > 1 ? "es" : ""} que enviar hoy
          </p>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        {["pendientes", "enviadas"].map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              tab === t ? "border-red-600 text-red-700" : "border-transparent text-slate-500 hover:text-slate-700"
            }`}
          >
            {t === "pendientes" ? `Pendientes (${pendientes.length})` : `Enviadas (${todas.filter(n => n.enviado).length})`}
          </button>
        ))}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          {tab === "pendientes" ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Medicamento</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Mensaje (preview)</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendientes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-10 text-slate-400">
                      No hay notificaciones pendientes
                    </TableCell>
                  </TableRow>
                ) : pendientes.map((n) => (
                  <TableRow key={n.id} className={esPasada(n.fecha_programada) ? "bg-red-50" : ""}>
                    <TableCell>
                      <p className="font-medium text-slate-800">{n.cliente_nombre}</p>
                      {n.zona && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-slate-400 mt-0.5">
                          <MapPin className="w-3 h-3" /> {n.zona}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">{n.medicamento_nombre}</TableCell>
                    <TableCell className="text-slate-500 text-sm">{n.telefono}</TableCell>
                    <TableCell>
                      <span className={`text-sm font-medium ${esPasada(n.fecha_programada) ? "text-red-600" : "text-slate-700"}`}>
                        {formatFecha(n.fecha_programada)}
                        {esPasada(n.fecha_programada) && <span className="ml-1 text-xs font-semibold">(HOY)</span>}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-[200px]">
                      <p className="text-xs text-slate-400 truncate">{n.mensaje?.slice(0, 60)}…</p>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEditOpen(n)} className="text-blue-600 hover:bg-blue-50 h-8 w-8 p-0" title="Editar mensaje">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => abrirEnviar(n)} className="text-green-600 hover:bg-green-50 h-8 px-2">
                          <Send className="w-4 h-4 mr-1" /> Enviar
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleRechazar(n)} className="text-orange-600 hover:bg-orange-50 h-8 px-2" title="El cliente rechazó su compra">
                          <ThumbsDown className="w-4 h-4 mr-1" /> Rechazó
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(n.id)} className="text-red-600 hover:bg-red-50 h-8 w-8 p-0">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cliente</TableHead>
                  <TableHead>Medicamento</TableHead>
                  <TableHead>Programada</TableHead>
                  <TableHead>Enviada</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {todas.filter(n => n.enviado).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-10 text-slate-400">No hay notificaciones enviadas</TableCell>
                  </TableRow>
                ) : todas.filter(n => n.enviado).map((n) => (
                  <TableRow key={n.id}>
                    <TableCell>
                      <p className="font-medium text-slate-800">{n.cliente_nombre}</p>
                      {n.zona && (
                        <span className="inline-flex items-center gap-0.5 text-xs text-slate-400 mt-0.5">
                          <MapPin className="w-3 h-3" /> {n.zona}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-600">{n.medicamento_nombre}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatFecha(n.fecha_programada)}</TableCell>
                    <TableCell className="text-sm text-slate-500">{formatFecha(n.fecha_envio)}</TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-xs text-green-600 font-medium">
                        <CheckCircle className="w-3 h-3" /> Enviada
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Diálogo envío manual */}
      <Dialog open={enviarOpen} onOpenChange={setEnviarOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-green-600" />
              Enviar a {notifSeleccionada?.cliente_nombre}
            </DialogTitle>
          </DialogHeader>
          {notifSeleccionada && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-600 space-y-1">
                <div>📱 {notifSeleccionada.telefono}</div>
                <div>
                  💊 {notifSeleccionada.medicamento_nombre}
                  {notifSeleccionada.ultima_compra_producto && (
                    <span className="ml-2 text-xs text-slate-400 font-normal">
                      (última compra: {notifSeleccionada.ultima_compra_producto})
                    </span>
                  )}
                </div>
                {notifSeleccionada.dosis_diaria && (
                  <div className="flex gap-4 pt-1 border-t border-slate-200 mt-1 text-xs font-medium">
                    <span>🗓 Dosis: <strong>{notifSeleccionada.dosis_diaria} pastilla{notifSeleccionada.dosis_diaria !== 1 ? "s" : ""}/día</strong></span>
                    <span>📦 Para 1 mes: <strong>{Math.round(notifSeleccionada.dosis_diaria * 30)} pastillas</strong></span>
                  </div>
                )}
              </div>

              <div className="bg-green-50 border border-green-100 rounded-lg p-3">
                <p className="text-xs text-green-700 font-medium mb-2">Mensaje para WhatsApp:</p>
                <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
                  {notifSeleccionada.mensaje}
                </p>
              </div>

              <div className="flex flex-col gap-2">
                <a
                  href={waLink(notifSeleccionada.telefono, notifSeleccionada.mensaje)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#20b858] text-white rounded-md px-4 py-2.5 text-sm font-medium transition-colors"
                >
                  <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" xmlns="http://www.w3.org/2000/svg">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  Abrir WhatsApp con el mensaje listo
                </a>

                <Button variant="outline" onClick={copiarMensaje} className="w-full border-slate-200">
                  {copiado
                    ? <><CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Copiado</>
                    : <><Copy className="w-4 h-4 mr-2" /> Copiar mensaje</>
                  }
                </Button>

                <Button onClick={marcarEnviado} className="w-full bg-slate-700 hover:bg-slate-800 text-white">
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Marcar como enviado
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Diálogo editar mensaje */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Editar mensaje de WhatsApp</DialogTitle>
          </DialogHeader>
          {editingNotif && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">
                Para: <strong>{editingNotif.cliente_nombre}</strong> · {editingNotif.telefono}
              </p>
              <div>
                <Label>Mensaje</Label>
                <Textarea
                  value={mensaje}
                  onChange={(e) => setMensaje(e.target.value)}
                  rows={8}
                  className="font-mono text-sm"
                />
              </div>
              <Button onClick={handleSaveMensaje} className="w-full bg-red-700 hover:bg-red-800">Guardar mensaje</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
